/**
 * My tasks — the cross-entity view.
 *
 * The only task screen not scoped to a record: `taskListQuery` makes the subject
 * optional precisely so this can span every entity.
 */

import { useState } from 'react'
import { Form, useFetcher, useLoaderData, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { TaskListResult, TaskStatus } from '@erp/contracts'
import { api } from '../api'
import { handleSubjectIntent } from '../detail-actions'
import { usePagedRows } from '../paging'
import { Badge, Button, EmptyState, Field, PageHeader, Select, Table, TextInput, Toolbar } from '../ui'
import { useOwnerOptions } from '../status-context'
import { contactName, displayName, formatDateTime, formatMoney, isOverdue } from '../format'


const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
}

const STATUS_COLOR: Record<TaskStatus, string> = {
  backlog: 'gray',
  todo: 'blue',
  in_progress: 'orange',
  done: 'green',
  cancelled: 'red',
}

interface TasksData {
  page: TaskListResult
  subjects: { value: string; label: string; group: string }[]
}

function fetchPage(request: Request): Promise<TaskListResult> {
  const url = new URL(request.url)
  return api.tasks.list({
    openOnly: url.searchParams.get('all') !== 'true',
    dueBefore: url.searchParams.get('due') ?? undefined,
    cursor: url.searchParams.get('cursor') ?? undefined,
    sort: 'dueDate',
    direction: 'asc',
    limit: 50,
  })
}

/** Fetcher-only route: just the page, which is what usePagedRows expects. */
export const pageLoader = ({ request }: LoaderFunctionArgs) => fetchPage(request)

/**
 * Every task hangs off exactly one record, so creating one here needs a record
 * to attach it to. One grouped select rather than a type-then-record cascade:
 * fewer controls, and no round trip between the two choices.
 *
 * Capped at 100 of each kind — past that this wants the same search-backed
 * combobox the organization and contact pickers are waiting on.
 */
export async function loader({ request }: LoaderFunctionArgs): Promise<TasksData> {
  const [page, leads, deals, organizations, contacts] = await Promise.all([
    fetchPage(request),
    api.leads.list({ limit: 100, sort: 'updatedAt', direction: 'desc' }),
    api.deals.list({ limit: 100, state: 'open' }),
    api.organizations.list({ limit: 100, sort: 'name', direction: 'asc' }),
    api.contacts.list({ limit: 100, sort: 'lastName', direction: 'asc' }),
  ])

  // A deal has no name of its own, so it is identified by its customer. Bare
  // amounts are not something anyone can pick from.
  const organizationName = new Map(organizations.rows.map((o) => [o.id, o.name]))

  const subjects = [
    ...leads.rows.map((lead) => ({
      value: `lead:${lead.id}`,
      label: displayName(lead),
      group: 'Leads',
    })),
    ...deals.rows.map((deal) => {
      const customer =
        (deal.organizationId ? organizationName.get(deal.organizationId) : null) ?? 'Unlinked deal'
      const amount = deal.amount !== null ? ` — ${formatMoney(deal.amount, deal.currency)}` : ''
      return { value: `deal:${deal.id}`, label: `${customer}${amount}`, group: 'Deals' }
    }),
    ...organizations.rows.map((organization) => ({
      value: `organization:${organization.id}`,
      label: organization.name,
      group: 'Organizations',
    })),
    ...contacts.rows.map((contact) => ({
      value: `contact:${contact.id}`,
      label: contactName(contact),
      group: 'Contacts',
    })),
  ]

  return { page, subjects }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()

  // The grouped select carries "type:id" in one value; split it back into the
  // subject pair the shared handler expects.
  const related = String(form.get('related') ?? '')
  if (related.includes(':')) {
    const [type, id] = related.split(':')
    form.set('subjectType', type ?? '')
    form.set('subjectId', id ?? '')
  }

  const result = await handleSubjectIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

export default function Tasks() {
  const { page, subjects } = useLoaderData() as TasksData
  const [searchParams] = useSearchParams()
  const fetcher = useFetcher()
  const { rows, cursor, loading, loadMore } = usePagedRows(page, '/crm/tasks/page')

  const showAll = searchParams.get('all') === 'true'
  const [creating, setCreating] = useState(false)
  // Inactive people stay listed while still holding a task, so reassigning
  // is a deliberate act rather than a side effect of someone leaving.
  const assignees = useOwnerOptions(null)

  return (
    <>
      <PageHeader
        title="Tasks"
        actions={
          <Button onClick={() => setCreating(!creating)}>
            {creating ? 'Cancel' : 'New task'}
          </Button>
        }
      />

      {creating ? <NewTaskForm subjects={subjects} /> : null}

      <Toolbar>
        <Form method="get">
          <label className="ax-checkbox">
            <input type="checkbox" name="all" value="true" defaultChecked={showAll} />
            Include closed
          </label>
          <Button type="submit">Apply</Button>
        </Form>
        <span className="ax-toolbar-count">
          {page.totalCount} {page.totalCount === 1 ? 'task' : 'tasks'}
        </span>
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState title={showAll ? 'No tasks' : 'Nothing open — inbox zero'} />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th scope="col">Task</th>
                <th scope="col">On</th>
                <th scope="col">Due</th>
                <th scope="col">Priority</th>
                <th scope="col">Assignee</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((task) => (
                <tr key={task.id} className={isOverdue(task.dueDate) ? 'ax-row-overdue' : undefined}>
                  <td>{task.title}</td>
                  <td>
                    <a href={`/crm/${task.subject.type}s/${task.subject.id}`}>{task.subject.type}</a>
                  </td>
                  <td>{task.dueDate ? formatDateTime(task.dueDate) : '—'}</td>
                  <td>{task.priority}</td>
                  <td>
                    <Select
                      name={`assignee-${task.id}`}
                      value={task.assignedTo ?? ''}
                      options={assignees}
                      onChange={(next) =>
                        fetcher.submit(
                          { intent: 'task.assign', taskId: task.id, assignedTo: next },
                          { method: 'post' },
                        )
                      }
                    />
                  </td>
                  <td>
                    <div className="ax-cell-inline">
                      <Badge color={STATUS_COLOR[task.status]}>{STATUS_LABEL[task.status]}</Badge>
                      <Select
                        name={`status-${task.id}`}
                        value={task.status}
                        options={Object.entries(STATUS_LABEL).map(([value, label]) => ({
                          value,
                          label,
                        }))}
                        onChange={(next) =>
                          fetcher.submit(
                            { intent: 'task.status', taskId: task.id, status: next },
                            { method: 'post' },
                          )
                        }
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {cursor ? (
            <div className="ax-list-footer">
              <Button onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}




/**
 * Creating a task from the cross-entity view. `related` is a grouped select
 * whose value packs the subject type and id together; the action unpacks it.
 */
function NewTaskForm({ subjects }: { subjects: TasksData['subjects'] }) {
  const assignees = useOwnerOptions(null)
  const groups = [...new Set(subjects.map((s) => s.group))]

  return (
    <Form method="post" className="ax-task-form">
      <input type="hidden" name="intent" value="task.create" />
      <Field label="Title">
        <TextInput name="title" placeholder="What needs doing?" required />
      </Field>
      <Field label="Related to">
        <select className="ax-select" name="related" required defaultValue="">
          <option value="" disabled>
            Select a record…
          </option>
          {groups.map((group) => (
            <optgroup key={group} label={group}>
              {subjects
                .filter((subject) => subject.group === group)
                .map((subject) => (
                  <option key={subject.value} value={subject.value}>
                    {subject.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </Field>
      <Field label="Due">
        <TextInput name="dueDate" type="datetime-local" />
      </Field>
      <Field label="Priority">
        <Select
          name="priority"
          value="medium"
          options={[
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
        />
      </Field>
      <Field label="Assign to">
        <Select name="assignedTo" value="" options={assignees} />
      </Field>
      <Button variant="solid" type="submit">
        Create task
      </Button>
    </Form>
  )
}
